import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTeamData } from './useTeamData';
import { MemberList, InviteDialog, PendingInvitationList } from './components';

type TeamTab = 'members' | 'invitations';

export default function TeamPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const companyId = id ? parseInt(id) : null;
  const [activeTab, setActiveTab] = useState<TeamTab>('members');

  const {
    members,
    currentUserId,
    loading,
    error,
    pendingInvitations,
    invite,
    changeRole,
    removeMember,
    cancelInvitation,
  } = useTeamData({ companyId });

  const currentMember = members.find((m) => m.userId === currentUserId);
  const isOwner = currentMember?.role === 'OWNER';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 0.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/companies/${id}/home`)}
          size="small"
        >
          На головну
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          Команда
        </Typography>
      </Box>

      {isOwner && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => invite.setOpen(true)}
          >
            Запросити
          </Button>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {invite.successMessage && <Alert severity="success" sx={{ mb: 2 }}>{invite.successMessage}</Alert>}

      {isOwner && (
        <Tabs
          value={activeTab}
          onChange={(_, value: TeamTab) => setActiveTab(value)}
          sx={{ mb: 1, minHeight: 40 }}
        >
          <Tab value="members" label={`Учасники (${members.length})`} sx={{ minHeight: 40 }} />
          <Tab
            value="invitations"
            label={pendingInvitations.length > 0 ? `Запрошені (${pendingInvitations.length})` : 'Запрошені'}
            sx={{ minHeight: 40 }}
          />
        </Tabs>
      )}

      <Paper variant="outlined">
        {(!isOwner || activeTab === 'members') && (
          <MemberList
            members={members}
            currentUserId={currentUserId}
            isOwner={isOwner ?? false}
            onRemove={removeMember}
            onChangeRole={changeRole}
          />
        )}
        {isOwner && activeTab === 'invitations' && (
          <PendingInvitationList invitations={pendingInvitations} onCancel={cancelInvitation} />
        )}
      </Paper>

      <InviteDialog
        open={invite.open}
        loading={invite.loading}
        error={invite.error}
        onClose={() => invite.setOpen(false)}
        onInvite={invite.submit}
      />
    </Box>
  );
}
